// Libraries
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// MUI
// Core
import { ResourceTextApplication } from '../../../../../core/resources';

// Common
import ViewLoader from '../../../../../common/ViewLoader';

// Interfaces
import {
    IAnimalAnnouncement,
    IAnimalAnnouncementNotificationData,
} from '../../../../../interfaces/IAnnouncement';
import {
    AnimalFilterTypeEnum,
    AnnouncementTypeEnum,
    DialogModeEnum,
    RoutePaths,
} from '../../../../../interfaces/enums';
import { IAnimalSummaryExtraInfo } from '../../../../../interfaces/IAnimal';

// Store
// API
import animalAnnouncementApi from '../../../../../api/animalAnnouncementApi';
import animalListApi from '../../../../../api/animalListApi';

// Feature
import BovineSlaughterAnnouncement from '../BovineSlaughterAnnouncement';
import BovineIntermediationAnnouncement from '../../bovine-intermediation-announcement/BovineIntermediationAnnouncement';
import ConfirmBovinePurchaseAnnouncement from '../../bovine-purchase-announcement/confirm/ConfirmBovinePurchaseAnnouncement';
import { IAnimalGroup, IMainGroup } from '../../../../../interfaces/IGroup';
import groupsApi from '../../../../../api/groupsApi';
import utils from '../../../../../core/utils';
import moment from 'moment';
import BovinePurchaseAnnouncement from '../../bovine-purchase-announcement/BovinePurchaseAnnouncement';
import { getAnimalGroup } from '../../../helpers/data';
import productionPlanApi from '../../../../../api/productionPlanApi';

interface IConfirmRelatedAnnouncement {
    announcement: IAnimalAnnouncement | null;
    openDialog: boolean;
}

export default function ApproveBovineAnnouncementNotification() {
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaGeneral']);

    const location = useLocation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState<boolean>(false);
    const [dialogIsOpen, setDialogIsOpen] = useState<boolean>(true);
    const [hasValidProductionPlan, setHasValidProductionPlan] = useState<boolean>(true);
    const [bovineAnnouncement, setBovineAnnouncement] = useState<IAnimalAnnouncement | null>(null);
    const [notificationData] = useState<IAnimalAnnouncementNotificationData | null>(
        getNotificationData()
    );
    const [animals, setAnimals] = useState<IAnimalSummaryExtraInfo[]>([]);
    const [confirmBovinePurchaseAnnouncement, setConfirmBovinePurchaseAnnouncement] =
        useState<IConfirmRelatedAnnouncement>({ announcement: null, openDialog: false });

    useEffect(() => {
        if (notificationData) {
            setLoading(true);
            Promise.all([
                getAnimalAnnouncement(notificationData.AnnouncementId),
                getFilteredAnimals(notificationData.FarmId),
                getHasValidProductionPlan(notificationData.FarmId),
            ]).then(() => setLoading(false));
        }
    }, [notificationData]);

    const getAnimalAnnouncement = (id: string) => {
        return animalAnnouncementApi.getAnimalAnnouncementsAnelma([id]).then((response) => {
            if (!response) {
                enqueueSnackbar(t('AnelmaSolmuErrors:1063'), { variant: 'error' });
                setLoading(false);
                return;
            }
            setBovineAnnouncement(response.Items[0]);
        });
    };

    function getNotificationData() {
        return location.state !== null && location.state.options.AnnouncementId
            ? ({
                  AnnouncementId: location.state.options.AnnouncementId,
                  FarmId: location.state.options.FarmId,
              } as IAnimalAnnouncementNotificationData)
            : null;
    }

    const getFilteredAnimals = (farmId: string) => {
        return animalListApi
            .getFilteredAnimals(farmId)
            .then(async (response) => {
                if (response && response?.Items?.length > 0) {
                    let animals = response.Items
                        .find((_) => _.FilterType === AnimalFilterTypeEnum.All)
                        ?.Animals as IAnimalSummaryExtraInfo[];

                    await groupsApi
                        .getGroups(farmId)
                        .then((groups) => {
                            if (groups) {
                                setAnimals(countExternalFields(animals, groups.Items));
                            }
                        })
                        .catch(() => {
                            enqueueSnackbar(t('AnelmaGeneral:1020'), {
                                variant: 'error',
                            });
                        })
                }
            })
            .catch(() => {
                enqueueSnackbar(t('AnelmaGeneral:1020'), {
                    variant: 'error',
                });
            })
    };

    const getHasValidProductionPlan = (farmId: string) => {
        if (farmId && farmId.length > 0)
            return productionPlanApi
                .farmHasValidProductionPlan(farmId)
                .then((response) => {
                    if (response && response.Entity) {
                        setHasValidProductionPlan(response.Entity.Valid);

                        if (!response.Entity.Valid) {
                            // no valid production plan
                            enqueueSnackbar(t('AnelmaSolmuErrors:1060'), {
                                variant: 'warning',
                            });
                        }
                    }
                    else {
                        setHasValidProductionPlan(false);
                    }
                })
                .catch(() => {
                    enqueueSnackbar(t('AnelmaGeneral:1020'), {
                        variant: 'error',
                    });
                })
    }

    const countExternalFields = (animals: IAnimalSummaryExtraInfo[], groups: IMainGroup[]) => {
        if (animals !== null) {
            let res = [...animals];

            res.forEach((a, i) => {
                a.Eu4 =
                    a.EuIdentifier?.length > 0
                        ? a.EuIdentifier.substring(a.EuIdentifier.length - 6, a.EuIdentifier.length - 2)
                        : null;

                a.AgeMonths = utils.calculateAgeInMonths(moment(), moment(a.BirthDate), 30.33);
                a.Group = getAnimalGroup(a.Id, groups);
            });

            return res;
        }

        return animals;
    }

    const openConfirmBovinePurchaceAnnouncement = (openPurchaseForConfirmation?: boolean) => {
        if (openPurchaseForConfirmation && bovineAnnouncement) {
            animalAnnouncementApi
                .getRelatedAnnouncement(bovineAnnouncement.Id)
                .then((response) => {
                    if (!response) {
                        enqueueSnackbar(t('AnelmaGeneral:1020'), {
                            variant: 'error',
                        });

                        return;
                    }

                    if (response.Entity) {
                        setDialogIsOpen(false);

                        setConfirmBovinePurchaseAnnouncement({
                            announcement: response.Entity,
                            openDialog: true,
                        });
                    }
                    else
                        navigate(RoutePaths.AnimalsList);
                });
        }
        else
            navigate(RoutePaths.AnimalsList);
    };

    const getAnnouncementJsx = (type: AnnouncementTypeEnum) => {
        if (type === AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement) {
            return (
                <BovineSlaughterAnnouncement
                    data={bovineAnnouncement}
                    onClose={(openPurchaseForConfirmation?: boolean) =>
                        openConfirmBovinePurchaceAnnouncement(openPurchaseForConfirmation)}
                    mode={DialogModeEnum.Edit}
                    processAnnouncementSave={() => false}
                    processAnnouncementDelete={() => false}
                    selectedAnimals={null}
                    animals={animals}
                    mapUpdatedSalesTypeToAllAnimalsData={() => false}
                    loading={false}
                    canCreateAnimalAnnouncement={null}
                    readOnly={!hasValidProductionPlan}
                    announcementConfirmation
                />
            );
        } else if (type === AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement) {
            return (
                <BovineIntermediationAnnouncement
                    data={bovineAnnouncement}
                    onClose={(openPurchaseForConfirmation?: boolean) =>
                        openConfirmBovinePurchaceAnnouncement(openPurchaseForConfirmation)}
                    mode={DialogModeEnum.Edit}
                    processAnnouncementSave={() => false}
                    processAnnouncementDelete={() => false}
                    selectedAnimals={null}
                    animals={animals}
                    mapUpdatedSalesTypeToAllAnimalsData={() => false}
                    loading={false}
                    canCreateAnimalAnnouncement={null}
                    readOnly={!hasValidProductionPlan}
                    announcementConfirmation
                />
            );
        } else if (type === AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement) {
            return (
                <BovinePurchaseAnnouncement
                    data={bovineAnnouncement}
                    onClose={() => navigate(RoutePaths.AnimalsList)}
                    mode={DialogModeEnum.Edit}
                    processAnnouncementSave={() => false}
                    processAnnouncementDelete={() => false}
                    readonly={!hasValidProductionPlan}
                    announcementConfirmation
                    weekReadonly
                />
            );
        } else return "";
    };

    return (
        <>
            {!loading && bovineAnnouncement && animals ? (
                <>
                    {dialogIsOpen && getAnnouncementJsx(bovineAnnouncement.Type)}

                    {confirmBovinePurchaseAnnouncement.openDialog && (
                        <ConfirmBovinePurchaseAnnouncement
                            announcement={confirmBovinePurchaseAnnouncement.announcement}
                            onClose={() => navigate(RoutePaths.AnimalsList)}
                            // doesn't have an access to any announcement list
                            processAnnouncementSave={() => { }}
                            processAnnouncementDelete={() => { }}
                        />
                    )}
                </>
            ) : (
                <ViewLoader />
            )}
        </>
    );
}
